import React from "react";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { sleep } from "../tools";
import { Permission, UserData } from "./auth";
import { useUserManager } from "../components/EnsureLogin";
import { User, UserManager } from "oidc-client-ts";
import { useTranslationsData } from "../components/TranslationsProvider";
import { FetchFunction, useFetch } from "../components/FetchProvider";

const UserDataContext = createContext(undefined);

function createRequestOptions(user: User): RequestInit {
    return {
        headers: {
            Authorization: `Bearer ${user.access_token}`,
        },
        mode: "cors",
    };
}

type ServerUserData = {
    user_id: number;
    account_page: string;
    permissions: Permission[];
};

async function fetchUserData(userManager: UserManager, fetch: FetchFunction): Promise<UserData> {
    const user = await userManager.getUser();
    if (user === null) return null;

    const url = window.location.origin + "/api/user-data";

    const response: Response = await fetch(url, createRequestOptions(user));

    const responseData = await response.json();

    if (response.status === 200) {
        const serverUserData = responseData as ServerUserData;
        return new UserData(
            serverUserData.user_id,
            serverUserData.account_page,
            user.profile,
            serverUserData.permissions,
        );
    } else if (response.status === 401 || responseData.error === "access_token_expired") {
        console.debug("Access token has expired");
        try {
            await userManager.signinSilent();
        } catch (err) {
            console.debug("Refreshing the access token has failed - reload to initiate full signin");
            userManager.removeUser();
            window.location.reload();
            // wait for the reload to happen
            await sleep(5000);
        }
        return await fetchUserData(userManager, fetch);
    } else if (response.status === 404 || responseData.error === "unknown_access_token") {
        console.debug("Access token is unknown");
        userManager.removeUser();
        window.location.reload();
        // wait for the reload to happen
        await sleep(5000);
    }
}

export function useUserData(): UserData {
    return useContext(UserDataContext).userData;
}

type OnUserDataChange = () => Promise<void>;

export function useOnUserDataChange(): OnUserDataChange {
    return useContext(UserDataContext).onUserDataChange;
}

function userStoreKey() {
    const settings = window.oidcClientSettings;
    return `oidc.user:${settings.authority}:${settings.client_id}`;
}

/**
 * Context provider that fetches the roles from Heimdall.
 *
 * Use useAuth() to access the authorization information.
 */
export const UserDataProvider = ({ children }: { children: ReactNode }) => {
    const [userData, setUserData] = useState<UserData>(null);
    const userManager = useUserManager();
    const { setUserLocale } = useTranslationsData();
    const fetch = useFetch();

    const fetchAndSetUserData = async () =>
        fetchUserData(userManager, fetch)
            .then((data: UserData) => {
                console.debug("User data arrived", data);
                setUserData(data);
                if (data === null) return;
                setUserLocale(data.profile.locale);
            })
            .catch((e) => console.error("Failed to fetch user data", e));

    useEffect(() => {
        fetchAndSetUserData();
    }, []);

    const onUserDataChange = async () => {
        userManager.startSilentRenew();
        userManager.signinSilent().then(fetchAndSetUserData).catch(console.error);
    };

    return <UserDataContext.Provider value={{ userData, onUserDataChange }}>{children}</UserDataContext.Provider>;
};
